import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  selectIsSubmitting,
  selectRemainingQuestions,
  selectSurveyAnswers,
  setDietGoal
} from "../../redux/survey/macroSurveySlice";
import {DietGoal} from "../../models/fitnessProfile";
import {LocalizationInfo} from "react-i18next";
import {FireColorIcon, FlexColorIcon, YogaColorIcon} from "../icons";
import {MacroSurveyOptionItemView} from "./MacroSurveyOptionItemView";
import {HeroHeading2} from "../text";
import {Button} from "../buttons/Button";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const MacroSurveyDietGoalQuestionView: React.FC<any> = ({  }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const answers = useAppSelector(selectSurveyAnswers)
  const remainingQuestions = useAppSelector(selectRemainingQuestions)
  const isSubmitting = useAppSelector(selectIsSubmitting)

  const getTitle = (dietGoal: DietGoal): LocalizationInfo => {
    switch (dietGoal) {
      case DietGoal.cut:
        return { key: 'macro_survey:macro_survey_title_burn_fat' }
      case DietGoal.bulk:
        return { key: 'macro_survey:macro_survey_title_build_muscle' }
      case DietGoal.maintenance:
        return { key: 'macro_survey:macro_survey_title_be_healthier' }
    }
  }

  const getIcon = (dietGoal: DietGoal): React.ReactNode => {
    switch (dietGoal) {
      case DietGoal.cut:
        return <FireColorIcon />
      case DietGoal.bulk:
        return <FlexColorIcon />
      case DietGoal.maintenance:
        return <YogaColorIcon />
    }
  }

  const options: DietGoal[] = [DietGoal.cut, DietGoal.bulk, DietGoal.maintenance]
  const [dietGoalAnswer, setDietGoalAnswer] = useState<DietGoal | undefined>(answers.goal)
  const [isValid, setIsValid] = useState<boolean>(false)

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Nutrition Survey' }, { 'Survey Question': 'Diet Goal' }))
  }, [])

  useEffect(() => {
    if (dietGoalAnswer) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [dietGoalAnswer])

  return <div className="flex flex-col space-y-8 lg:space-y-10 items-center w-full">
    <HeroHeading2 className="text-center mx-8">{t('macro_survey:macro_survey_header_title_diet_goal')}</HeroHeading2>

    <div className="w-full">
      {options.map(option => {
        return <div key={option.toString()} className="border-b border-fg-gray-surface last:border-b-0">
          <MacroSurveyOptionItemView
            item={{
              title: getTitle(option),
              id: option.toString(),
              isSelected: dietGoalAnswer === option
            }}
            icon={getIcon(option)}
            isLoading={false}
            onItemSelected={() => {
              setDietGoalAnswer(option)
            }}
          />
        </div>
      })}
    </div>

    <div className="w-full px-5 md:justify-center md:flex">
      <Button
        isDisabled={!isValid}
        isLoading={isSubmitting}
        size="lg"
        isFullWidth={true}
        onClick={() => {
          dietGoalAnswer && dispatch(setDietGoal(dietGoalAnswer))
        }}
      >
        {remainingQuestions.length ? t('common:common_title_continue') : t('common:common_title_submit')}
      </Button>
    </div>
  </div>
}